import { useTranslation } from 'react-i18next';

import { SectionCard, formatNumber } from '../../../components/design-system/admin';
import { PROVIDER_NAMES, type CostDays, type ProviderView } from '../../../common/models/platform-costs.model';
import { SpendChange, money } from './cost-format';
import { colourOf, rankProviders } from './SpendChart';

/** Each provider's share of the period's spend, and how it moved against the period before. */
export function ShareCard({ providers, days, className }: { providers: ProviderView[]; days: CostDays; className?: string }) {
  const { t } = useTranslation();
  const ranked = rankProviders(providers);
  const rows = ranked.map((id) => providers.find((p) => p.provider === id)!).filter(Boolean);
  const total = rows.reduce((s, p) => s + p.windowUsd, 0) || 1;

  return (
    <SectionCard
      title={t('ADMIN_COSTS.SHARE.TITLE')}
      description={t('ADMIN_COSTS.SHARE.DESCRIPTION')}
      action={<span className="text-xs text-muted-foreground">{t('ADMIN_COSTS.SHARE.PERIOD', { days })}</span>}
      className={className}
      testId="costs-share"
    >
      <div className="flex h-2.5 overflow-hidden rounded-full bg-muted" aria-hidden>
        {rows.map((p) => (
          <div key={p.provider} style={{ width: `${(p.windowUsd / total) * 100}%`, background: colourOf(ranked, p.provider) }} />
        ))}
      </div>
      <ul className="mt-4 flex flex-col gap-3">
        {rows.map((p) => (
          <li key={p.provider} className="flex items-center justify-between gap-3 text-sm">
            <span className="flex min-w-0 items-center gap-2">
              <span className="size-2.5 shrink-0 rounded-[3px]" style={{ background: colourOf(ranked, p.provider) }} aria-hidden />
              <span className="truncate">{PROVIDER_NAMES[p.provider]}</span>
              <span className="text-xs text-muted-foreground tabular-nums">{formatNumber((p.windowUsd / total) * 100, 0)}%</span>
            </span>
            <span className="flex items-baseline gap-2 tabular-nums">
              <span className="font-medium">{money(p.windowUsd)}</span>
              <SpendChange value={p.windowUsd} previous={p.prevWindowUsd} />
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
